import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, UserPlus, Users, X } from 'lucide-react';
import { canManageAkteMembers, coercePortalAkteMemberAccess } from './portalCustomerAccess';
import type { PortalRole } from './portalCustomerAccess';
import {
  addPortalAkteMember,
  listKanzleiSeatsForAktePicker,
  listPortalAkteMembers,
  removePortalAkteMember,
  updatePortalAkteMemberAccess,
} from './portalAkten';
import { noteSitzGlockePortalAction, sitzGlockeActorFromSeat } from './sitzGlockeStore';
import type {
  KanzleiSeatForAktePicker,
  PortalAkteMemberAccess,
  PortalAkteMemberRecord,
  PortalAkteRecord,
} from './portalAktenTypes';

export interface PortalAkteMitgliederPanelProps {
  akte: PortalAkteRecord;
  authToken: string;
  actorId: string;
  kanzleiId: string;
  portalRole?: PortalRole;
  isPrimary?: boolean;
}

function accessLabel(access: PortalAkteMemberAccess): string {
  return access === 'write' ? 'Schreiben' : 'Lesen';
}

export const PortalAkteMitgliederPanel: React.FC<PortalAkteMitgliederPanelProps> = ({
  akte,
  authToken,
  actorId,
  kanzleiId,
  portalRole,
  isPrimary,
}) => {
  const [members, setMembers] = useState<PortalAkteMemberRecord[]>([]);
  const [seats, setSeats] = useState<KanzleiSeatForAktePicker[]>([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [addId, setAddId] = useState('');
  const [addAccess, setAddAccess] = useState<PortalAkteMemberAccess>('read');
  const [message, setMessage] = useState<string | null>(null);

  const canManage = canManageAkteMembers(
    { id: actorId, portal_role: portalRole, is_primary: isPrimary },
    akte,
  );

  const reload = useCallback(async () => {
    if (!authToken || !kanzleiId) return;
    setLoading(true);
    try {
      const rows = await listPortalAkteMembers(authToken, akte.id, kanzleiId);
      setMembers(rows);
      if (canManage) {
        setSeats(await listKanzleiSeatsForAktePicker(authToken, kanzleiId));
      }
    } finally {
      setLoading(false);
    }
  }, [authToken, akte.id, kanzleiId, canManage]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const memberIds = useMemo(() => new Set(members.map((m) => m.customer_id)), [members]);
  const addable = useMemo(
    () => seats.filter((s) => s.id && !memberIds.has(s.id)),
    [seats, memberIds],
  );

  const seatById = useMemo(() => {
    const map = new Map<string, KanzleiSeatForAktePicker>();
    for (const s of seats) map.set(s.id, s);
    return map;
  }, [seats]);

  const handleAdd = useCallback(async () => {
    if (!addId) return;
    setBusyId('add');
    setMessage(null);
    try {
      const seat = seats.find((s) => s.id === addId);
      const result = await addPortalAkteMember(authToken, {
        akte_id: akte.id,
        kanzlei_id: kanzleiId,
        customer_id: addId,
        access: coercePortalAkteMemberAccess({
          access: addAccess,
          targetRole: seat?.portal_role,
          isAktenfuehrer: addId === akte.aktenfuehrer_id,
        }),
      });
      if (result.ok === false) {
        setMessage(result.error);
        return;
      }
      const actor = sitzGlockeActorFromSeat(seatById.get(actorId) || seats.find((s) => s.id === actorId));
      noteSitzGlockePortalAction({
        targetSeatId: addId,
        actorSeatId: actorId,
        actorName: actor?.name,
        actorRole: actor?.role,
        source: 'akte',
        refId: akte.id,
        kind: 'added',
      });
      setAddId('');
      await reload();
    } finally {
      setBusyId(null);
    }
  }, [addId, addAccess, authToken, akte.id, akte.aktenfuehrer_id, actorId, kanzleiId, reload, seatById, seats]);

  const handleAccess = useCallback(
    async (row: PortalAkteMemberRecord, access: PortalAkteMemberAccess) => {
      setBusyId(row.id);
      setMessage(null);
      try {
        const result = await updatePortalAkteMemberAccess(authToken, row.id, access);
        if (result.ok === false) {
          setMessage(result.error);
          return;
        }
        setMembers((prev) => prev.map((m) => (m.id === row.id ? result.data : m)));
      } finally {
        setBusyId(null);
      }
    },
    [authToken],
  );

  const handleRemove = useCallback(
    async (row: PortalAkteMemberRecord) => {
      if (row.customer_id === akte.aktenfuehrer_id) return;
      setBusyId(row.id);
      setMessage(null);
      try {
        const result = await removePortalAkteMember(authToken, row.id);
        if (result.ok === false) {
          setMessage(result.error);
          return;
        }
        const actor = sitzGlockeActorFromSeat(seatById.get(actorId) || seats.find((s) => s.id === actorId));
        noteSitzGlockePortalAction({
          targetSeatId: row.customer_id,
          actorSeatId: actorId,
          actorName: actor?.name,
          actorRole: actor?.role,
          source: 'akte',
          refId: akte.id,
          kind: 'removed',
        });
        setMembers((prev) => prev.filter((m) => m.id !== row.id));
      } finally {
        setBusyId(null);
      }
    },
    [authToken, akte.aktenfuehrer_id, akte.id, actorId, seatById, seats],
  );

  return (
    <div className="rounded-lg border border-white/[0.08] bg-white/[0.02] px-2 py-2 space-y-2">
      <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
        <Users className="w-3 h-3" aria-hidden />
        Kreis — Sitze zuweisen
        {loading ? <Loader2 className="w-3 h-3 animate-spin text-slate-500" /> : null}
      </div>
      {members.length === 0 && !loading ? (
        <p className="text-[11px] text-slate-500 m-0">
          Noch niemand im Kreis — Aktenführer holt Sitze über „Person wählen“.
        </p>
      ) : (
        <ul className="m-0 p-0 list-none space-y-1">
          {members.map((row) => {
            const seat = seatById.get(row.customer_id);
            const isLead = row.customer_id === akte.aktenfuehrer_id;
            const label = seat?.name || seat?.email || row.customer_id.slice(0, 8);
            const praktikant = seat?.portal_role === 'praktikant';
            return (
              <li
                key={row.id}
                className="flex items-center gap-2 text-[11px] text-slate-300 min-w-0"
              >
                <span className="truncate flex-1 min-w-0" title={seat?.email || row.customer_id}>
                  {label}
                  {isLead ? ' · Aktenführer' : ''}
                  {praktikant ? ' · Praktikant (lesen)' : ''}
                </span>
                {canManage && !isLead ? (
                  <select
                    value={praktikant ? 'read' : row.access}
                    disabled={busyId === row.id || praktikant}
                    onChange={(e) =>
                      void handleAccess(row, e.target.value === 'write' ? 'write' : 'read')
                    }
                    className="rounded border border-white/[0.08] bg-slate-900/80 px-1 py-0.5 text-[10px] text-slate-300"
                    aria-label={`Recht für ${label}`}
                  >
                    <option value="read">Lesen</option>
                    <option value="write">Schreiben</option>
                  </select>
                ) : (
                  <span className="text-slate-500 flex-shrink-0">{accessLabel(row.access)}</span>
                )}
                {canManage && !isLead ? (
                  <button
                    type="button"
                    onClick={() => void handleRemove(row)}
                    disabled={busyId === row.id}
                    className="p-0.5 text-slate-500 hover:text-red-300 disabled:opacity-50"
                    title="Aus der Akte entfernen"
                    aria-label={`${label} entfernen`}
                  >
                    <X className="w-3 h-3" />
                  </button>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
      {canManage ? (
        <div className="flex flex-wrap items-center gap-1.5">
          <select
            value={addId}
            onChange={(e) => setAddId(e.target.value)}
            className="flex-1 min-w-[8rem] rounded border border-white/[0.08] bg-slate-900/80 px-1.5 py-0.5 text-[11px] text-slate-300"
            aria-label="Person aus der Kanzlei hinzufügen"
          >
            <option value="">Person wählen…</option>
            {addable.length === 0 ? (
              <option value="" disabled>
                {seats.length === 0
                  ? 'Keine Sitze in der Auswahl'
                  : 'Alle Sitze sind bereits im Kreis'}
              </option>
            ) : (
              addable.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name || s.email}
                  {s.portal_role === 'praktikant' ? ' (Praktikant)' : ''}
                </option>
              ))
            )}
          </select>
          <select
            value={addAccess}
            onChange={(e) => setAddAccess(e.target.value === 'write' ? 'write' : 'read')}
            className="rounded border border-white/[0.08] bg-slate-900/80 px-1 py-0.5 text-[10px] text-slate-300"
            aria-label="Recht"
          >
            <option value="read">Lesen</option>
            <option value="write">Schreiben</option>
          </select>
          <button
            type="button"
            onClick={() => void handleAdd()}
            disabled={!addId || busyId === 'add'}
            className="inline-flex items-center gap-1 rounded border border-white/[0.1] px-1.5 py-0.5 text-[10px] text-slate-300 hover:bg-white/[0.04] disabled:opacity-50"
          >
            {busyId === 'add' ? (
              <Loader2 className="w-3 h-3 animate-spin" />
            ) : (
              <UserPlus className="w-3 h-3" />
            )}
            Hinzufügen
          </button>
        </div>
      ) : null}
      {message ? <p className="text-[11px] text-amber-200/90 m-0">{message}</p> : null}
    </div>
  );
};
